<template>
  <div class="admin-doc-pages-container container mt-5">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h1 class="Title mb-0">Administration - Pages de Documentation</h1>
      <div class="header-buttons">
        <b-button variant="info" @click="openImportModal" class="me-2">
          <i class="fas fa-upload me-2"></i>Importer
        </b-button>
        <b-button variant="success" @click="openCreateModal">
          <i class="fas fa-plus me-2"></i>Créer une page
        </b-button>
      </div>
    </div>

    <!-- Filtres et recherche -->
    <div class="filters-container mb-3">
      <div class="d-flex gap-2 align-items-end flex-wrap">
        <div class="flex-grow-1" style="min-width: 250px;">
          <b-form-group label="Recherche" class="mb-0">
            <b-form-input
              v-model="searchQuery"
              placeholder="Rechercher par titre..."
              @input="debouncedSearch"
              debounce="500"
              size="sm"
            />
          </b-form-group>
        </div>
        
        <div class="dropdown">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i class="fas fa-filter me-2"></i>Filtres
            <span v-if="hasActiveFilters" class="badge bg-primary ms-2">{{ activeFiltersCount }}</span>
          </button>
          <ul class="dropdown-menu dropdown-menu-end p-3" style="min-width: 280px;">
            <li class="mb-2">
              <b-form-group label="Version" class="mb-0" label-size="sm">
                <b-form-select
                  v-model="selectedVersion"
                  :options="versionOptions"
                  @change="loadPages"
                  size="sm"
                />
              </b-form-group>
            </li>
            <li class="mb-2">
              <b-form-group label="Statut" class="mb-0" label-size="sm">
                <b-form-select
                  v-model="filterPublished"
                  :options="publishedOptions"
                  @change="loadPages"
                  size="sm"
                />
              </b-form-group>
            </li>
            <li>
              <hr class="dropdown-divider">
            </li>
            <li>
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm w-100"
                @click="resetFilters"
              >
                <i class="fas fa-redo me-2"></i>Réinitialiser
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Liste des pages -->
    <b-card>
      <div v-if="loading" class="text-center py-5">
        <b-spinner></b-spinner>
        <p class="mt-3">Chargement des pages...</p>
      </div>

      <div v-else-if="filteredPages.length === 0" class="text-center py-5">
        <i class="fas fa-file-alt fa-3x text-muted mb-3"></i>
        <p class="text-muted">Aucune page trouvée</p>
      </div>

      <b-table
        v-else
        :items="filteredPages"
        :fields="tableFields"
        striped
        hover
        responsive
        class="doc-pages-table"
      >
        <template #cell(title)="row">
          <div class="d-flex align-items-center">
            <span v-if="row.item.parent_id" class="text-muted me-2" style="font-size: 0.8rem;">└─</span>
            <strong class="page-title">{{ row.item.title }}</strong>
            <b-badge :variant="row.item.is_published ? 'success' : 'secondary'" class="ms-2 flex-shrink-0">
              {{ row.item.is_published ? 'Publié' : 'Brouillon' }}
            </b-badge>
          </div>
        </template>

        <template #cell(slug)="row">
          <code class="text-muted small">{{ row.item.slug }}</code>
        </template>

        <template #cell(parent)="row">
          <span v-if="row.item.parent_id" class="text-muted small">
            {{ getParentTitle(row.item.parent_id) }}
          </span>
          <span v-else class="text-muted small">—</span>
        </template>

        <template #cell(actions)="row">
          <div class="d-flex gap-1 justify-content-end">
            <b-button
              variant="outline-primary"
              size="sm"
              @click="viewPage(row.item.slug)"
              title="Voir la page"
              class="action-btn"
            >
              <i class="fas fa-eye"></i>
            </b-button>
            <b-button
              variant="outline-warning"
              size="sm"
              @click="openEditModal(row.item)"
              title="Modifier"
              class="action-btn"
            >
              <i class="fas fa-edit"></i>
            </b-button>
            <b-button
              variant="outline-danger"
              size="sm"
              @click="confirmDelete(row.item)"
              title="Supprimer"
              class="action-btn"
            >
              <i class="fas fa-trash"></i>
            </b-button>
          </div>
        </template>
      </b-table>
    </b-card>

    <!-- Modal de création/édition -->
    <b-modal
      v-model="showModal"
      :title="isEditing ? 'Modifier la page' : 'Créer une nouvelle page'"
      size="xl"
      @ok="savePage"
      @cancel="resetForm"
      @hidden="resetForm"
      :ok-disabled="(!form.slug || !form.title) || importing"
      scrollable
    >
      <b-form @submit.prevent="savePage">
        <b-form-group label="Slug *" description="URL unique de la page (ex: guide/installation)">
          <b-form-input
            v-model="form.slug"
            placeholder="guide/installation"
            required
            :disabled="isEditing"
          />
        </b-form-group>

        <b-form-group label="Titre *">
          <b-form-input
            v-model="form.title"
            placeholder="Titre de la page"
            required
          />
        </b-form-group>

        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Version">
              <b-form-input
                v-model="form.version"
                placeholder="latest"
              />
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Ordre">
              <b-form-input
                v-model.number="form.order_index"
                type="number"
                min="0"
              />
            </b-form-group>
          </div>
        </div>

        <b-form-group label="Page parente">
          <b-form-select
            v-model="form.parent_id"
            :options="parentPageOptions"
            :disabled="isEditing && form.id"
          >
            <template #first>
              <b-form-select-option :value="null">Aucune (page racine)</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

        <b-form-group>
          <b-form-checkbox v-model="form.is_published">
            Page publiée (visible pour les utilisateurs)
          </b-form-checkbox>
        </b-form-group>

        <b-form-group label="Contenu (Markdown) *">
          <b-form-textarea
            v-model="form.content_md"
            rows="15"
            placeholder="# Titre&#10;&#10;Contenu en Markdown..."
            required
            class="markdown-editor"
          />
          <small class="text-muted">
            Utilisez la syntaxe Markdown pour formater votre contenu
          </small>
        </b-form-group>

        <!-- Aperçu du rendu (optionnel) -->
        <b-form-group label="Aperçu">
          <div class="markdown-preview border p-3" v-html="renderedPreview"></div>
        </b-form-group>
      </b-form>
    </b-modal>

    <!-- Modal de confirmation de suppression -->
    <b-modal
      v-model="showDeleteModal"
      title="Confirmer la suppression"
      @ok="deletePage"
      ok-variant="danger"
    >
      <p>
        Êtes-vous sûr de vouloir supprimer la page <strong>{{ pageToDelete?.title }}</strong> ?
      </p>
      <p class="text-danger">
        <i class="fas fa-exclamation-triangle me-2"></i>
        <strong>Attention :</strong> Cette action est irréversible.
      </p>
      <p v-if="hasChildren(pageToDelete?.id)" class="text-warning">
        <i class="fas fa-info-circle me-2"></i>
        Cette page a des pages enfants. Vous devez d'abord supprimer ou déplacer les pages enfants.
      </p>
    </b-modal>

    <!-- Modal de visualisation -->
    <b-modal
      v-model="showViewModal"
      :title="viewingPage?.title || 'Visualisation'"
      size="xl"
      @hidden="viewingPage = null"
      ok-only
      ok-title="Fermer"
    >
      <div v-if="viewingPage" class="view-page-content">
        <div class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
          <div>
            <small class="text-muted">
              <i class="fas fa-link me-1"></i>
              Slug: <code>{{ viewingPage.slug }}</code>
            </small>
          </div>
          <div>
            <b-badge :variant="viewingPage.is_published ? 'success' : 'secondary'" class="me-2">
              {{ viewingPage.is_published ? 'Publié' : 'Brouillon' }}
            </b-badge>
            <small class="text-muted">
              <i class="fas fa-clock me-1"></i>
              {{ formatDate(viewingPage.updated_at) }}
            </small>
          </div>
        </div>
        <div class="doc-view-content" v-html="renderedViewContent"></div>
      </div>
      <div v-else class="text-center py-4">
        <b-spinner></b-spinner>
        <p class="mt-2">Chargement...</p>
      </div>
    </b-modal>

    <!-- Modal d'import -->
    <b-modal
      v-model="showImportModal"
      title="Importer des pages de documentation"
      size="lg"
      @ok="performImport"
      @cancel="resetImportForm"
      @hidden="resetImportForm"
      :ok-disabled="!importForm.file || importing"
      :ok-title="importing ? 'Import en cours...' : 'Importer'"
    >
      <b-form @submit.prevent="performImport">
        <b-form-group label="Fichier à importer *">
          <input
            type="file"
            class="form-control"
            accept=".zip,.rar,.md"
            @change="onFileSelected"
            ref="fileInput"
          />
          <small class="text-muted d-block mt-2">
            Formats acceptés : <code>.zip</code> ou <code>.rar</code> (dossier de fichiers .md) ou <code>.md</code> (fichier unique)
          </small>
        </b-form-group>

        <div v-if="importForm.file" class="file-info mb-3 p-3 bg-light rounded">
          <div class="d-flex align-items-center">
            <i class="fas fa-file-alt fa-2x me-3 text-primary"></i>
            <div>
              <strong>{{ importForm.file.name }}</strong>
              <br>
              <small class="text-muted">
                {{ formatFileSize(importForm.file.size) }}
              </small>
            </div>
          </div>
        </div>

        <b-form-group label="Nom de la documentation (page d'accueil)">
          <b-form-input
            v-model="importForm.rootTitle"
            placeholder="Ex: Documentation technique"
          />
          <small class="text-muted">Remplace le titre de <code>index.md</code> à la racine</small>
        </b-form-group>

        <b-form-group label="Préfixe de documentation (pour éviter les conflits)">
          <b-form-input
            v-model="importForm.baseSlug"
            placeholder="Ex: doc-client, v2, produit-x"
          />
          <small class="text-muted">
            Tous les slugs importés seront préfixés (ex: <code>doc-client/guide/installation</code>)
          </small>
        </b-form-group>

        <b-form-group label="Version">
          <b-form-input
            v-model="importForm.version"
            placeholder="latest"
          />
          <small class="text-muted">Version des pages importées (par défaut: latest)</small>
        </b-form-group>

        <b-form-group>
          <b-form-checkbox v-model="importForm.overwrite">
            Remplacer les pages existantes
          </b-form-checkbox>
          <small class="text-muted">
            Si coché, les pages avec le même slug seront mises à jour. Sinon, elles seront ignorées.
          </small>
        </b-form-group>

        <div class="alert alert-info">
          <i class="fas fa-info-circle me-2"></i>
          <strong>Structure recommandée :</strong>
          <ul class="mb-0 mt-2">
            <li>Pour un ZIP ou RAR : organisez vos fichiers .md dans des dossiers (ex: <code>guide/installation.md</code>)</li>
            <li>Le titre sera extrait du premier <code># Titre</code> dans chaque fichier</li>
            <li>La hiérarchie sera créée automatiquement selon la structure des dossiers</li>
            <li><strong>Note :</strong> Le support RAR nécessite l'installation de <code>node-unrar-js</code> sur le serveur</li>
          </ul>
        </div>

        <!-- Barre de progression -->
        <div v-if="importing" class="mt-4">
          <div class="d-flex align-items-center mb-2">
            <b-spinner small class="me-2"></b-spinner>
            <span class="text-primary"><strong>Import en cours...</strong></span>
          </div>
          <div class="progress" style="height: 25px;">
            <div
              class="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              :style="{ width: importProgress + '%' }"
              :aria-valuenow="importProgress"
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {{ importProgress }}%
            </div>
          </div>
          <small class="text-muted d-block mt-2">{{ importStatus }}</small>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import api from '@/plugins/api';
import { marked } from 'marked';

export default {
  name: 'AdminDocPagesPage',
  data() {
    return {
      pages: [],
      loading: false,
      searchQuery: '',
      selectedVersion: 'latest',
      filterPublished: 'all',
      versions: ['latest'],
      showModal: false,
      showDeleteModal: false,
      showImportModal: false,
      showViewModal: false,
      isEditing: false,
      pageToDelete: null,
      viewingPage: null,
      importForm: {
        file: null,
        rootTitle: '',
        baseSlug: '',
        version: 'latest',
        overwrite: false
      },
      importing: false,
      form: {
        id: null,
        slug: '',
        title: '',
        content_md: '',
        version: 'latest',
        parent_id: null,
        order_index: 0,
        is_published: true
      },
      tableFields: [
        { key: 'title', label: 'Titre', sortable: true },
        { key: 'slug', label: 'Slug', sortable: true },
        { key: 'parent', label: 'Parent', sortable: false },
        { key: 'actions', label: 'Actions', thClass: 'text-end', tdClass: 'text-end' }
      ],
      searchTimeout: null
    };
  },
  computed: {
    versionOptions() {
      return [
        { value: 'all', text: 'Toutes les versions' },
        ...this.versions.map(v => ({ value: v, text: v }))
      ];
    },
    publishedOptions() {
      return [
        { value: 'all', text: 'Tous' },
        { value: 'published', text: 'Publiées uniquement' },
        { value: 'draft', text: 'Brouillons uniquement' }
      ];
    },
    parentPageOptions() {
      // Exclure la page courante et ses enfants de la liste des parents
      const excludeIds = this.isEditing && this.form.id ? [this.form.id] : [];
      return this.pages
        .filter(p => !excludeIds.includes(p.id) && p.id !== this.form.id)
        .map(p => ({
          value: p.id,
          text: `${p.title} (${p.slug})`
        }));
    },
    hasActiveFilters() {
      return this.selectedVersion !== 'all' || this.filterPublished !== 'all' || this.searchQuery.trim() !== '';
    },
    activeFiltersCount() {
      let count = 0;
      if (this.selectedVersion !== 'all') count++;
      if (this.filterPublished !== 'all') count++;
      if (this.searchQuery.trim() !== '') count++;
      return count;
    },
    filteredPages() {
      let filtered = [...this.pages];

      // Afficher toutes les pages (pas seulement les racines)

      // Filtre par version
      if (this.selectedVersion !== 'all') {
        filtered = filtered.filter(p => p.version === this.selectedVersion);
      }

      // Filtre par statut
      if (this.filterPublished === 'published') {
        filtered = filtered.filter(p => p.is_published);
      } else if (this.filterPublished === 'draft') {
        filtered = filtered.filter(p => !p.is_published);
      }

      // Filtre par recherche
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(p =>
          p.title.toLowerCase().includes(query) ||
          p.slug.toLowerCase().includes(query)
        );
      }

      // Trier par parent_id (pages racines d'abord), puis par order_index et titre
      filtered.sort((a, b) => {
        // Pages racines en premier
        if (!a.parent_id && b.parent_id) return -1;
        if (a.parent_id && !b.parent_id) return 1;
        // Même niveau : trier par order_index puis par titre
        if (a.order_index !== b.order_index) {
          return (a.order_index || 0) - (b.order_index || 0);
        }
        return a.title.localeCompare(b.title);
      });

      return filtered;
    },
    renderedPreview() {
      if (!this.form.content_md) {
        return '<p class="text-muted">Aperçu du contenu...</p>';
      }
      try {
        return marked(this.form.content_md, {
          breaks: true,
          gfm: true
        });
      } catch (error) {
        return '<p class="text-danger">Erreur lors du rendu</p>';
      }
    },
    renderedViewContent() {
      if (!this.viewingPage?.content_md) {
        return '<p class="text-muted">Aucun contenu disponible</p>';
      }
      try {
        return marked(this.viewingPage.content_md, {
          breaks: true,
          gfm: true
        });
      } catch (error) {
        console.error('Erreur lors du rendu Markdown:', error);
        return '<p class="text-danger">Erreur lors du rendu du contenu</p>';
      }
    }
  },
  methods: {
    async loadPages() {
      this.loading = true;
      try {
        const params = {
          version: this.selectedVersion === 'all' ? null : this.selectedVersion,
          includeUnpublished: true
        };
        const response = await api.get('/doc-pages', { params });
        this.pages = response.data.data || [];
      } catch (error) {
        console.error('Erreur lors du chargement des pages:', error);
        this.$bvToast.toast('Erreur lors du chargement des pages', {
          title: 'Erreur',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    },
    async loadVersions() {
      try {
        const response = await api.get('/doc-pages/versions');
        this.versions = response.data.data || ['latest'];
      } catch (error) {
        console.error('Erreur lors du chargement des versions:', error);
      }
    },
    openCreateModal() {
      this.isEditing = false;
      this.resetForm();
      this.showModal = true;
    },
    openEditModal(page) {
      this.isEditing = true;
      this.form = {
        id: page.id,
        slug: page.slug,
        title: page.title,
        content_md: page.content_md || '',
        version: page.version || 'latest',
        parent_id: page.parent_id,
        order_index: page.order_index || 0,
        is_published: page.is_published !== false
      };
      this.showModal = true;
    },
    async savePage() {
      if (!this.form.slug || !this.form.title) {
        this.$bvToast.toast('Le slug et le titre sont requis', {
          title: 'Validation',
          variant: 'warning',
          solid: true
        });
        return;
      }

      try {
        if (this.isEditing) {
          const { id, ...updateData } = this.form;
          await api.put(`/doc-pages/${id}`, updateData);
          this.$bvToast.toast('Page modifiée avec succès', {
            title: 'Succès',
            variant: 'success',
            solid: true
          });
        } else {
          await api.post('/doc-pages', this.form);
          this.$bvToast.toast('Page créée avec succès', {
            title: 'Succès',
            variant: 'success',
            solid: true
          });
        }
        this.showModal = false;
        await this.loadPages();
        await this.loadVersions();
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error);
        const message = error.response?.data?.message || 'Erreur lors de la sauvegarde';
        this.$bvToast.toast(message, {
          title: 'Erreur',
          variant: 'danger',
          solid: true
        });
      }
    },
    confirmDelete(page) {
      this.pageToDelete = page;
      this.showDeleteModal = true;
    },
    async deletePage() {
      if (!this.pageToDelete) return;

      try {
        await api.delete(`/doc-pages/${this.pageToDelete.id}`);
        this.$bvToast.toast('Page supprimée avec succès', {
          title: 'Succès',
          variant: 'success',
          solid: true
        });
        this.showDeleteModal = false;
        this.pageToDelete = null;
        await this.loadPages();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        const message = error.response?.data?.message || 'Erreur lors de la suppression';
        this.$bvToast.toast(message, {
          title: 'Erreur',
          variant: 'danger',
          solid: true
        });
        this.showDeleteModal = false;
      }
    },
    async viewPage(slug) {
      try {
        this.loading = true;
        const response = await api.get(`/doc-pages/slug/${slug}`, {
          params: { version: this.selectedVersion === 'all' ? 'latest' : this.selectedVersion }
        });
        this.viewingPage = response.data.data;
        this.showViewModal = true;
      } catch (error) {
        console.error('Erreur lors du chargement de la page:', error);
        this.$bvToast.toast('Erreur lors du chargement de la page', {
          title: 'Erreur',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.form = {
        id: null,
        slug: '',
        title: '',
        content_md: '',
        version: 'latest',
        parent_id: null,
        order_index: 0,
        is_published: true
      };
      this.isEditing = false;
    },
    resetFilters() {
      this.searchQuery = '';
      this.selectedVersion = 'latest';
      this.filterPublished = 'all';
    },
    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        // La recherche se fait via computed filteredPages
      }, 500);
    },
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    getParentTitle(parentId) {
      const parent = this.pages.find(p => p.id === parentId);
      return parent ? parent.title : 'Inconnu';
    },
    hasChildren(pageId) {
      return this.pages.some(p => p.parent_id === pageId);
    },
    openImportModal() {
      this.showImportModal = true;
    },
    onFileSelected(event) {
      const file = event.target.files[0];
      if (file) {
        this.importForm.file = file;
      }
    },
    async performImport() {
      if (!this.importForm.file) {
        this.$bvToast.toast('Veuillez sélectionner un fichier', {
          title: 'Validation',
          variant: 'warning',
          solid: true
        });
        return;
      }

      this.importing = true;
      this.importProgress = 0;
      this.importStatus = 'Préparation de l\'import...';

      try {
        // Simulation de progression pour l'upload
        this.importProgress = 10;
        this.importStatus = 'Envoi du fichier au serveur...';

        const formData = new FormData();
        formData.append('file', this.importForm.file);
        formData.append('rootTitle', this.importForm.rootTitle || '');
        formData.append('version', this.importForm.version || 'latest');
        formData.append('overwrite', this.importForm.overwrite);
        formData.append('baseSlug', this.importForm.baseSlug || '');

        this.importProgress = 30;
        this.importStatus = 'Traitement du fichier...';

        const response = await api.post('/doc-pages/import', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              this.importProgress = 30 + Math.round(percentCompleted * 0.3); // 30% à 60%
            }
          }
        });

        this.importProgress = 70;
        this.importStatus = 'Création des pages dans la base de données...';

        const result = response.data.data;
        
        this.importProgress = 90;
        this.importStatus = 'Finalisation...';

        let message = `${result.imported} page(s) importée(s) avec succès`;
        
        if (result.errors > 0) {
          message += `, ${result.errors} erreur(s)`;
        }

        this.importProgress = 100;
        this.importStatus = 'Import terminé !';

        // Attendre un peu pour voir la barre à 100%
        await new Promise(resolve => setTimeout(resolve, 500));

        this.$bvToast.toast(message, {
          title: result.errors > 0 ? 'Import partiel' : 'Import réussi',
          variant: result.errors > 0 ? 'warning' : 'success',
          solid: true,
          autoHideDelay: 5000
        });

        if (result.errors_details && result.errors_details.length > 0) {
          console.warn('Erreurs d\'import:', result.errors_details);
        }

        this.showImportModal = false;
        
        // Recharger la liste automatiquement
        this.importStatus = 'Rechargement de la liste...';
        await this.loadPages();
        await this.loadVersions();
        
        this.$bvToast.toast('Liste mise à jour', {
          title: 'Succès',
          variant: 'success',
          solid: true,
          autoHideDelay: 2000
        });
      } catch (error) {
        console.error('Erreur lors de l\'import:', error);
        this.importProgress = 0;
        this.importStatus = 'Erreur lors de l\'import';
        const message = error.response?.data?.message || 'Erreur lors de l\'import';
        this.$bvToast.toast(message, {
          title: 'Erreur',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.importing = false;
        this.importProgress = 0;
        this.importStatus = '';
      }
    },
    resetImportForm() {
      this.importForm = {
        file: null,
        rootTitle: '',
        baseSlug: '',
        version: 'latest',
        overwrite: false
      };
      this.importing = false;
      this.importProgress = 0;
      this.importStatus = '';
    },
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
  },
  async mounted() {
    await this.loadVersions();
    await this.loadPages();
  }
};
</script>

<style scoped>
.admin-doc-pages-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
}

.filters-container {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 0.375rem;
}

.doc-pages-table {
  font-size: 0.9rem;
}

.doc-pages-table .page-title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.doc-pages-table .action-btn {
  min-width: 36px;
  width: 36px;
  height: 36px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.doc-pages-table .action-btn i {
  font-size: 0.875rem;
}

.doc-pages-table tbody tr {
  transition: background-color 0.15s ease;
}

.doc-pages-table tbody tr:hover {
  background-color: #f8f9fa;
}

/* Amélioration de l'affichage PC */
@media (min-width: 768px) {
  .doc-pages-table {
    font-size: 0.95rem;
  }
  
  .doc-pages-table th,
  .doc-pages-table td {
    padding: 0.75rem 1rem;
    vertical-align: middle;
  }
  
  .doc-pages-table .action-btn {
    min-width: 40px;
    width: 40px;
    height: 40px;
  }
}

/* Styles pour la barre de progression d'import */
.progress {
  border-radius: 0.375rem;
  overflow: hidden;
}

.progress-bar {
  font-weight: 600;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.view-page-content {
  max-height: 70vh;
  overflow-y: auto;
}

.doc-view-content {
  line-height: 1.8;
  padding: 1rem 0;
}

.doc-view-content :deep(h1),
.doc-view-content :deep(h2),
.doc-view-content :deep(h3),
.doc-view-content :deep(h4),
.doc-view-content :deep(h5),
.doc-view-content :deep(h6) {
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  color: #2c3e50;
  font-weight: 600;
}

.doc-view-content :deep(h1) {
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.5rem;
  font-size: 2rem;
}

.doc-view-content :deep(h2) {
  font-size: 1.75rem;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 0.3rem;
}

.doc-view-content :deep(h3) {
  font-size: 1.5rem;
}

.doc-view-content :deep(h4) {
  font-size: 1.25rem;
}

.doc-view-content :deep(p) {
  margin-bottom: 1rem;
  color: #495057;
}

.doc-view-content :deep(code) {
  background-color: #f4f4f4;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-size: 0.9em;
  color: #e83e8c;
  font-family: 'Courier New', monospace;
}

.doc-view-content :deep(pre) {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
  border-left: 4px solid #007bff;
  margin: 1rem 0;
}

.doc-view-content :deep(pre code) {
  background-color: transparent;
  padding: 0;
  color: #333;
  display: block;
}

.doc-view-content :deep(blockquote) {
  border-left: 4px solid #007bff;
  padding-left: 1rem;
  margin-left: 0;
  color: #6c757d;
  font-style: italic;
  background-color: #f8f9fa;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  margin: 1rem 0;
}

.doc-view-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  font-size: 0.9rem;
}

.doc-view-content :deep(table th),
.doc-view-content :deep(table td) {
  border: 1px solid #dee2e6;
  padding: 0.75rem;
  text-align: left;
}

.doc-view-content :deep(table th) {
  background-color: #f8f9fa;
  font-weight: 600;
}

.doc-view-content :deep(ul),
.doc-view-content :deep(ol) {
  margin-bottom: 1rem;
  padding-left: 2rem;
}

.doc-view-content :deep(li) {
  margin-bottom: 0.5rem;
  color: #495057;
}

.doc-view-content :deep(a) {
  color: #007bff;
  text-decoration: none;
  font-weight: 500;
}

.doc-view-content :deep(a:hover) {
  text-decoration: underline;
}

.doc-view-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 1rem 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.doc-view-content :deep(hr) {
  border: none;
  border-top: 2px solid #e9ecef;
  margin: 2rem 0;
}

.doc-view-content :deep(strong) {
  font-weight: 600;
  color: #2c3e50;
}

.doc-view-content :deep(em) {
  font-style: italic;
  color: #495057;
}

.markdown-editor {
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
}

.markdown-preview {
  max-height: 300px;
  overflow-y: auto;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.markdown-preview :deep(h1),
.markdown-preview :deep(h2),
.markdown-preview :deep(h3) {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.markdown-preview :deep(code) {
  background-color: #f4f4f4;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-size: 0.9em;
}

.markdown-preview :deep(pre) {
  background-color: #f8f9fa;
  padding: 0.5rem;
  border-radius: 4px;
  overflow-x: auto;
}

@media (max-width: 768px) {
  .admin-doc-pages-container {
    padding: 0 0.5rem;
  }

  .d-flex.justify-content-between {
    flex-direction: column;
    gap: 1rem;
  }

  .d-flex.justify-content-between .btn {
    width: 100%;
  }
}
</style>

